import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { adminCancelEnrollment } from "@/modules/eventRegistration/features/EventWaitlist/api/adminCancelEnrollment/adminCancelEnrollmentApi";
import { reinstateEnrollment } from "@/modules/eventRegistration/features/EventWaitlist/api/reinstateEnrollment/reinstateEnrollmentApi";
import useWaitlist from "@/modules/eventRegistration/features/EventWaitlist/hooks/useWaitlist";
import RosterTab, {
  type RosterFilter,
} from "@/modules/eventRegistration/features/EventWaitlist/components/RosterTab/RosterTab";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import ConfirmRemoveWaitlistModal from "@/modules/eventRegistration/features/EventWaitlist/components/ConfirmRemoveWaitlistModal/ConfirmRemoveWaitlistModal";
import ConfirmRegisterWaitlistModal from "@/modules/eventRegistration/features/EventWaitlist/components/ConfirmRegisterWaitlistModal/ConfirmRegisterWaitlistModal";
import ConfirmMoveEnrollmentToWaitlistModal from "@/modules/eventRegistration/features/EventWaitlist/components/ConfirmMoveEnrollmentToWaitlistModal/ConfirmMoveEnrollmentToWaitlistModal";
import ConfirmReplaceEnrollmentModal from "@/modules/eventRegistration/features/EventWaitlist/components/ConfirmReplaceEnrollmentModal/ConfirmReplaceEnrollmentModal";
import Attendances from "@/modules/eventRegistration/features/EventWaitlist/components/Attendances/Attendances";
import FinalizeSessionModal from "@/modules/eventRegistration/features/EventWaitlist/components/FinalizeSessionModal/FinalizeSessionModal";
import useEventTransactions from "@/modules/eventRegistration/features/EventWaitlist/hooks/useEventTransactions/useEventTransactions";
import { TransactionsView } from "@/modules/eventRegistration/features/EventWaitlist/components/TransactionsView";
import { SessionSwitcher } from "@/modules/eventRegistration/features/EventWaitlist/components/SessionSwitcher/SessionSwitcher";
import { SessionPaymentRequestsView } from "@/modules/eventRegistration/features/PaymentRequests/components/SessionPaymentRequestsView/SessionPaymentRequestsView";

interface TabOption {
  value: string;
  label: string;
  badge: number;
  alertCount?: number;
}

export default function Waitlist() {
  const { uuid } = useParams<{ uuid: string; eventUuid: string }>();
  const {
    activeTab,
    isRefreshing,
    isProcessing,
    waitlist,
    registeredUsers,
    waitlistDetails,
    loadingAttendanceGrid,
    loadingEnrollments,
    loadingWaitlistItems,
    attendanceGridItems,
    attendanceGridSessionDays,
    showRemoveDialog,
    showMoveToWaitlistDialog,
    showReplaceDialog,
    showRegisterDialog,
    selectedUser,
    selectedRegistration,
    handleTabChange,
    refreshData,
    removeFromWaitlist,
    registerFromWaitlist,
    moveFromEnrollmentToWaitlist,
    updateWaitlistPosition,
    moveWaitlistItemToTop,
    formatDate,
    setShowRemoveDialog,
    setShowMoveToWaitlistDialog,
    setShowReplaceDialog,
    setShowRegisterDialog,
    handleRegisterClick,
    handleRemoveClick,
    handleReplaceClick,
    handleMoveToWaitlistClick,
    handleExportAttendanceCsv,
    isExportingCsv,
    updateAttendance,
    bulkUpdateAttendanceStatus,
    isUpdatingAttendance,
    showFinalizeSessionDialog,
    setShowFinalizeSessionDialog,
    finalizeSession,
  } = useWaitlist({
    eventSessionUuid: uuid,
  });

  const {
    enrollmentBillingItems,
    isLoading: isTransactionsLoading,
    refreshData: refreshTransactions,
  } = useEventTransactions({ eventUuid: uuid });

  const isSessionFull =
    !!waitlistDetails && waitlistDetails.availableSeatCount <= 0;

  const selectedUserFullName = selectedUser
    ? [selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(" ")
    : "Unknown";

  const selectedRegistrationFullName = selectedRegistration
    ? [selectedRegistration.firstName, selectedRegistration.lastName]
        .filter(Boolean)
        .join(" ")
    : "Unknown";

  const tabOptions: TabOption[] = [
    {
      value: "roster",
      label: "Roster",
      badge: registeredUsers.length + waitlist.length,
    },
    {
      value: "attendance",
      label: "Attendance",
      badge: registeredUsers.length,
    },
    {
      value: "billing",
      label: "Billing",
      badge: enrollmentBillingItems.length,
      alertCount: enrollmentBillingItems.filter(
        (item) => item.lifecycleState === "cancelled" && !item.isRefunded,
      ).length,
    },
    {
      value: "payment-requests",
      label: "Payment Requests",
      badge: enrollmentBillingItems.filter(
        (item) => item.pendingPaymentRequest !== null,
      ).length,
    },
  ];

  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [rosterFilter, setRosterFilter] = useState<RosterFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Reset selected day when navigating to a different session
  React.useEffect(() => {
    setSelectedDay(null);
  }, [uuid]);

  // Auto-select first day when attendance data loads
  React.useEffect(() => {
    if (attendanceGridSessionDays.length > 0) {
      setSelectedDay(attendanceGridSessionDays[0]);
    }
  }, [attendanceGridSessionDays]);

  const handleTabSelect = (value: string) => {
    handleTabChange(value);
    if (value === "billing") {
      refreshTransactions();
    }
  };

  const { eventUuid } = useParams<{ eventUuid: string }>();
  const navigate = useNavigate();

  // Filter by search query
  const filteredAttendanceGridItems = React.useMemo(() => {
    if (!searchQuery.trim()) return attendanceGridItems;
    const q = searchQuery.toLowerCase();
    return attendanceGridItems.filter(
      (item) =>
        item.attendee?.fullName?.toLowerCase().includes(q) ||
        item.companyName?.toLowerCase().includes(q),
    );
  }, [attendanceGridItems, searchQuery]);

  const filteredRegisteredUsers = React.useMemo(() => {
    if (!searchQuery.trim()) return registeredUsers;
    const q = searchQuery.toLowerCase();
    return registeredUsers.filter(
      (u) =>
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.companyName?.toLowerCase().includes(q),
    );
  }, [registeredUsers, searchQuery]);

  const filteredWaitlist = React.useMemo(() => {
    if (!searchQuery.trim()) return waitlist;
    const q = searchQuery.toLowerCase();
    return waitlist.filter(
      (u) =>
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.companyName?.toLowerCase().includes(q),
    );
  }, [waitlist, searchQuery]);

  const filteredBillingItems = React.useMemo(() => {
    if (!searchQuery.trim()) return enrollmentBillingItems;
    const q = searchQuery.toLowerCase();
    return enrollmentBillingItems.filter(
      (item) =>
        `${item.firstName} ${item.lastName}`.toLowerCase().includes(q) ||
        item.email?.toLowerCase().includes(q) ||
        item.companyName?.toLowerCase().includes(q),
    );
  }, [enrollmentBillingItems, searchQuery]);

  // Roster filter counts
  const activeUsers = registeredUsers.filter((u) => !u.isCancelled);
  const cancelledUsers = registeredUsers.filter((u) => u.isCancelled);

  const uniqueCompanies = React.useMemo(() => {
    const set = new Set<string>();
    registeredUsers.forEach((u) => {
      if (u.companyName?.trim()) set.add(u.companyName.trim());
    });
    waitlist.forEach((u) => {
      if (u.companyName?.trim()) set.add(u.companyName.trim());
    });
    return set.size;
  }, [registeredUsers, waitlist]);

  const rosterFilterOptions: { value: RosterFilter; label: string }[] = [
    { value: "all", label: `All (${activeUsers.length + waitlist.length})` },
    { value: "registered", label: `Registered (${activeUsers.length})` },
    { value: "waitlisted", label: `Waitlisted (${waitlist.length})` },
    { value: "cancelled", label: `Cancelled (${cancelledUsers.length})` },
  ];

  const { toast } = useToast();

  const handleCancelEnrollment = async (user: (typeof registeredUsers)[0]) => {
    const reason = window.prompt("Reason for cancellation:");
    if (reason === null) return;

    try {
      await adminCancelEnrollment(user.id, reason);
      toast({
        title: "Enrollment cancelled",
        description: `${user.firstName} ${user.lastName} has been cancelled.`,
      });
      await refreshData();
      refreshTransactions();
    } catch (e: any) {
      toast({
        title: "Error",
        description:
          e?.response?.data?.errors?.detail || "Failed to cancel enrollment",
        variant: "destructive",
      });
    }
  };

  const handleReinstateEnrollment = async (
    user: (typeof registeredUsers)[0],
  ) => {
    if (!window.confirm(`Reinstate ${user.firstName} ${user.lastName}?`))
      return;

    try {
      await reinstateEnrollment(user.id);
      toast({
        title: "Enrollment reinstated",
        description: `${user.firstName} ${user.lastName} has been reinstated.`,
      });
      await refreshData();
      refreshTransactions();
    } catch (e: any) {
      toast({
        title: "Error",
        description:
          e?.response?.data?.errors?.detail || "Failed to reinstate enrollment",
        variant: "destructive",
      });
    }
  };

  const sessionDateLabel = (() => {
    if (!waitlistDetails?.startDate) return "";
    const start = new Date(waitlistDetails.startDate);
    const startFmt = start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const tz = waitlistDetails.timezoneShortName;
    return tz ? `${startFmt} · ${tz}` : startFmt;
  })();

  return (
    <MainPageWrapper>
      {/* Page header — event/session identity + global session switcher */}
      {waitlistDetails && (
        <div className="mb-3">
          <SessionSwitcher
            currentEventName={waitlistDetails.eventName ?? "Event"}
            currentEventUuid={waitlistDetails.eventUuid ?? eventUuid ?? ""}
            currentSessionDateLabel={sessionDateLabel || "—"}
            currentSessionName={waitlistDetails.name}
            currentSessionUuid={waitlistDetails.uuid}
            currentVenueName={null}
            trailingPath="waitlist"
          />
        </div>
      )}

      {/* Compact toolbar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap px-4 py-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg">
        <Button
          className="h-8 w-8 flex-shrink-0"
          onClick={() => navigate(`/admin/events/${eventUuid}/sessions`)}
          size="icon"
          variant="ghost"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>

        {/* Tab pills */}
        {tabOptions.map((tab) => (
          <button
            className={`px-3 py-1 rounded-md text-xs font-medium border transition-colors ${
              activeTab === tab.value
                ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-gray-900 dark:border-white"
                : "bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700"
            }`}
            key={tab.value}
            onClick={() => handleTabSelect(tab.value)}
            type="button"
          >
            {tab.label}
            {tab.alertCount != null && tab.alertCount > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-medium leading-none">
                {tab.alertCount}
              </span>
            )}
          </button>
        ))}

        {/* Roster sub-filters */}
        {activeTab === "roster" && (
          <>
            <div className="w-px h-5 bg-gray-200 dark:bg-slate-700 mx-1" />
            {rosterFilterOptions.map((opt) => (
              <button
                className={`px-2 py-1 rounded-md text-xs font-medium border transition-colors ${
                  rosterFilter === opt.value
                    ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-gray-900 dark:border-white"
                    : "bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700"
                }`}
                key={opt.value}
                onClick={() => setRosterFilter(opt.value)}
                type="button"
              >
                {opt.label}
              </button>
            ))}
          </>
        )}

        {/* Day picker — only on attendance tab, hidden for single-day events */}
        {activeTab === "attendance" && attendanceGridSessionDays.length > 1 && (
          <>
            <div className="w-px h-5 bg-gray-200 dark:bg-slate-700 mx-1" />
            {attendanceGridSessionDays.map((day) => {
              const [year, month, d] = day.split("-").map(Number);
              const dateObj = new Date(year, month - 1, d);
              const label = dateObj.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });
              const isActive = selectedDay === day;

              // Check if this day has any pending attendance
              const hasPending = attendanceGridItems.some((item) => {
                const ds = item.dayStatuses.find((s) => s.date === day);
                return ds?.status === "pending";
              });
              const allResolved = !hasPending && attendanceGridItems.length > 0;

              return (
                <button
                  className={`px-2 py-1 rounded-md text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                    isActive
                      ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-gray-900 dark:border-white"
                      : "bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700"
                  }`}
                  key={day}
                  onClick={() => setSelectedDay(day)}
                  type="button"
                >
                  {label}
                  {hasPending && (
                    <span
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        isActive ? "bg-amber-400" : "bg-amber-500"
                      }`}
                    />
                  )}
                  {allResolved && (
                    <span
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        isActive ? "bg-green-400" : "bg-green-500"
                      }`}
                    />
                  )}
                </button>
              );
            })}
          </>
        )}

        {/* Right-aligned actions */}
        <div className="flex items-center gap-2 ml-auto">
          {activeTab === "roster" && waitlistDetails && (
            <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">
              <span className="font-medium text-gray-900 dark:text-white">
                {registeredUsers.filter((u) => !u.isCancelled).length}
              </span>
              {" / "}
              {waitlistDetails.maxEnrollments} seats
            </span>
          )}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 dark:text-gray-500" />
            <input
              className="h-7 w-44 pl-7 pr-2 text-xs rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-slate-600"
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              type="text"
              value={searchQuery}
            />
          </div>
          {activeTab === "attendance" && (
            <>
              <Button
                className="h-7 text-xs"
                disabled={isExportingCsv}
                onClick={handleExportAttendanceCsv}
                size="sm"
                variant="outline"
              >
                {isExportingCsv ? "Exporting..." : "Export"}
              </Button>

              {!waitlistDetails?.isSessionFinalized &&
                registeredUsers.length > 0 && (
                  <Button
                    className="h-7 text-xs"
                    onClick={() => setShowFinalizeSessionDialog(true)}
                    size="sm"
                    variant="default"
                  >
                    Finalize
                  </Button>
                )}
            </>
          )}
        </div>
      </div>

      <Tabs
        className="w-full"
        onValueChange={handleTabSelect}
        value={activeTab}
      >
        <TabsContent value="roster">
          <RosterTab
            formatDate={formatDate}
            handleCancelClick={handleCancelEnrollment}
            handleMoveToWaitlistClick={handleMoveToWaitlistClick}
            handleRegisterClick={handleRegisterClick}
            handleReinstateClick={handleReinstateEnrollment}
            handleRemoveClick={handleRemoveClick}
            handleReplaceClick={handleReplaceClick}
            isSessionFull={isSessionFull}
            loading={loadingEnrollments || loadingWaitlistItems}
            onMoveToTop={moveWaitlistItemToTop}
            onPositionChange={updateWaitlistPosition}
            registeredUsers={filteredRegisteredUsers}
            rosterFilter={rosterFilter}
            waitlist={filteredWaitlist}
            waitlistDetails={waitlistDetails}
          />
        </TabsContent>

        <TabsContent value="attendance">
          <Attendances
            gridItems={filteredAttendanceGridItems}
            isSessionFinalized={waitlistDetails?.isSessionFinalized}
            key={selectedDay}
            loading={loadingAttendanceGrid}
            onRefresh={refreshData}
            selectedDay={selectedDay}
            sessionDays={attendanceGridSessionDays}
            updateAttendance={updateAttendance}
            waitlistDetails={waitlistDetails}
          />
        </TabsContent>

        <TabsContent value="billing">
          <TransactionsView
            enrollments={filteredBillingItems}
            isLoading={isTransactionsLoading}
            onRefundSuccess={refreshTransactions}
          />
        </TabsContent>

        <TabsContent value="payment-requests">
          {uuid ? (
            <SessionPaymentRequestsView
              billingItems={enrollmentBillingItems}
              onBillingRefresh={refreshTransactions}
              sessionUuid={uuid}
            />
          ) : null}
        </TabsContent>
      </Tabs>

      {/* WAITLIST MODALS */}
      <ConfirmRemoveWaitlistModal
        isOpen={showRemoveDialog}
        onClose={() => setShowRemoveDialog(false)}
        onConfirm={async (removalReason?: string) => {
          if (selectedUser) {
            await removeFromWaitlist(selectedUser.id, removalReason);
            refreshTransactions();
          }
        }}
        userFullName={selectedUserFullName}
      />

      <ConfirmRegisterWaitlistModal
        isOpen={showRegisterDialog}
        onClose={() => setShowRegisterDialog(false)}
        onConfirm={async (sendNotification: boolean) => {
          if (selectedUser) {
            await registerFromWaitlist(selectedUser.id, sendNotification);
            refreshTransactions();
          }
        }}
        userFullName={selectedUserFullName}
      />

      <ConfirmMoveEnrollmentToWaitlistModal
        isOpen={showMoveToWaitlistDialog}
        onClose={() => setShowMoveToWaitlistDialog(false)}
        onConfirm={async (sendNotification: boolean) => {
          if (selectedRegistration) {
            await moveFromEnrollmentToWaitlist(
              selectedRegistration.id,
              sendNotification,
            );
            refreshTransactions();
          }
        }}
        userFullName={selectedRegistrationFullName}
      />

      <ConfirmReplaceEnrollmentModal
        eventUuid={uuid}
        isOpen={showReplaceDialog}
        onClose={() => setShowReplaceDialog(false)}
        onSuccess={async () => {
          await refreshData();
          refreshTransactions();
        }}
        selectedRegistration={selectedRegistration}
      />

      <FinalizeSessionModal
        isOpen={showFinalizeSessionDialog}
        onClose={() => setShowFinalizeSessionDialog(false)}
        onConfirm={finalizeSession}
        waitlistCount={waitlist.length}
      />
    </MainPageWrapper>
  );
}
